import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Footer sections data
const FOOTER_SECTIONS = {
  products: [
    { name: "TadReamk Eye", url: "/products/tadreamk-eye" },
    { name: "TadReamk Patent", url: "/products/tadreamk-patent" },
    { name: "TadReamk Create", url: "/products/tadreamk-create" },
    { name: "Trademark Trading", url: "/products/trademark-trading" },
    { name: "TadReamk Surveillance", url: "/products/tadreamk-surveillance" },
    { name: "Business Card Manager", url: "/products/business-card-manager" },
  ],
  aiServices: [{ name: "Lace Material", url: "/ai-services/lace-material" }],
  company: [
    { name: "About Us", url: "/about" },
    { name: "Careers", url: "/careers" },
  ],
  articles: [
    { name: "News", url: "/news" },
    { name: "IP Law", url: "/magazine/law" },
    { name: "TadReamk AI", url: "/magazine/ai" },
    { name: "TadReamk Design", url: "/magazine/design" },
    { name: "TadReamk AIGC", url: "/magazine/aigc" },
    { name: "TadReamk ERP", url: "/magazine/erp" },
  ],
  legal: [
    { name: "Privacy Policy", url: "/privacy-policy" },
    { name: "Terms of Service", url: "/terms-of-service" },
    { name: "Cookie Policy", url: "/cookie-policy" },
    { name: "GDPR", url: "/gdpr" },
  ],
  business: [
    { name: "Offers", url: "/business/overview" },
    { name: "API Documentation", url: "/business/api" },
    { name: "Contact Sales", url: "/business/contact" },
    { name: "Pricing", url: "/business/pricing" },
  ],
};

// Social media links data
const SOCIAL_LINKS = [
  {
    name: "LinkedIn",
    url: "https://www.linkedin.com/company/tadreamk/",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: "GitHub",
    url: "https://github.com/tadreamk/tadreamk-python",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
      </svg>
    ),
  },
];

// Company Logo Component
const CompanyLogo = ({ logoUrl }) => {
  return (
    <div className="flex items-center space-x-3 mb-4">
      <img
        src={logoUrl}
        alt="TadReamk Logo"
        className="h-10 w-10 object-contain"
      />
      <span className="text-2xl font-bold text-white">TadReamk</span>
    </div>
  );
};

// Company Description Component
const CompanyDescription = () => {
  const { t } = useTranslation("common");
  return (
    <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-md">
      {t("company.description")}
    </p>
  );
};

// Social Links Component
const SocialLinks = () => {
  return (
    <div className="flex space-x-4">
      {SOCIAL_LINKS.map((social, index) => (
        <a
          key={index}
          href={social.url}
          className="w-10 h-10 bg-gray-800/50 hover:bg-gradient-to-r hover:from-violet-600 hover:to-purple-600 border border-gray-700/50 hover:border-violet-400/50 rounded-xl flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300 hover:scale-105"
          title={social.name}
        >
          {social.icon}
        </a>
      ))}
    </div>
  );
};

// Company Info Component
const CompanyInfo = ({ logoUrl }) => {
  return (
    <div className="lg:col-span-2">
      <div className="mb-6">
        <CompanyLogo logoUrl={logoUrl} />
        <CompanyDescription />
        <SocialLinks />
      </div>
    </div>
  );
};

// Footer Section Component
const FooterSection = ({ title, links }) => {
  const { t } = useTranslation("common");

  // Map English link names to translation keys
  const translateLinkName = (name) => {
    const linkMap = {
      // Products
      "TadReamk Eye": t("products.tadreamkEye"),
      "TadReamk Patent": t("products.tadreamkPatent"),
      "TadReamk Create": t("products.tadreamkCreate"),
      "Trademark Trading": t("products.trademarkTrading"),
      "TadReamk Surveillance": t("products.tadreamkSurveillance"),
      "Business Card Manager": t("products.businessCardManager"),
      // AI Services
      "Lace Material": t("products.laceMaterial"),
      // Company
      "About Us": t("companyLinks.aboutUs"),
      Careers: t("companyLinks.careers"),
      // Articles
      News: t("navigation.news"),
      "IP Law": t("magazine.ipLaw"),
      "TadReamk AI": t("magazine.tadreamkAI"),
      "TadReamk Design": t("magazine.tadreamkDesign"),
      // Legal
      "Privacy Policy": t("footer.legal.privacyPolicy"),
      "Terms of Service": t("footer.legal.termsOfService"),
      "Cookie Policy": t("footer.legal.cookiePolicy"),
      GDPR: t("footer.legal.gdpr"),
      // Business
      Offers: t("business.offers"),
      "API Documentation": t("business.apiDocumentation"),
      "Contact Sales": t("business.contactSales"),
      Pricing: t("business.pricing"),
    };
    return linkMap[name] || name;
  };

  return (
    <div>
      <h4 className="text-lg font-semibold text-white mb-4">{title}</h4>
      <ul className="space-y-3">
        {links.map((link, index) => (
          <li key={index}>
            {link.url.startsWith("/") ? (
              <Link
                to={link.url}
                className="text-gray-300 hover:text-violet-300 transition-colors duration-300 text-sm"
              >
                {translateLinkName(link.name)}
              </Link>
            ) : (
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-violet-300 transition-colors duration-300 text-sm"
              >
                {translateLinkName(link.name)}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Footer Links Grid Component
const FooterLinksGrid = () => {
  const { t } = useTranslation("common");
  return (
    <>
      <FooterSection
        title={t("footer.sections.products")}
        links={FOOTER_SECTIONS.products}
      />
      <FooterSection
        title={t("footer.sections.business")}
        links={FOOTER_SECTIONS.business}
      />
      <FooterSection
        title={t("footer.sections.aiServices")}
        links={FOOTER_SECTIONS.aiServices}
      />
      <FooterSection
        title={t("footer.sections.articles")}
        links={FOOTER_SECTIONS.articles}
      />
      <FooterSection
        title={t("footer.sections.company")}
        links={FOOTER_SECTIONS.company}
      />
    </>
  );
};

// Mobile Legal Links Component
const MobileLegalLinks = () => {
  const { t } = useTranslation("common");
  return (
    <div className="md:hidden mt-8">
      <FooterSection
        title={t("footer.sections.legal")}
        links={FOOTER_SECTIONS.legal}
      />
    </div>
  );
};

// Copyright Component
const Copyright = ({ currentYear }) => {
  const { t } = useTranslation("common");
  return (
    <div className="text-sm text-gray-400">
      {t("footer.copyright", { year: currentYear })}
    </div>
  );
};

// Desktop Legal Links Component
const DesktopLegalLinks = () => {
  const { t } = useTranslation("common");

  const translateLinkName = (name) => {
    const linkMap = {
      "Privacy Policy": t("footer.legal.privacyPolicy"),
      "Terms of Service": t("footer.legal.termsOfService"),
      "Cookie Policy": t("footer.legal.cookiePolicy"),
      GDPR: t("footer.legal.gdpr"),
    };
    return linkMap[name] || name;
  };

  return (
    <div className="hidden md:flex space-x-6">
      {FOOTER_SECTIONS.legal.map((link, index) =>
        link.url.startsWith("/") ? (
          <Link
            key={index}
            to={link.url}
            className="text-sm text-gray-400 hover:text-violet-300 transition-colors duration-300"
          >
            {translateLinkName(link.name)}
          </Link>
        ) : (
          <a
            key={index}
            href={link.url}
            className="text-sm text-gray-400 hover:text-violet-300 transition-colors duration-300"
          >
            {translateLinkName(link.name)}
          </a>
        ),
      )}
    </div>
  );
};

// Footer Bottom Component
const FooterBottom = ({ currentYear }) => {
  return (
    <div className="border-t border-gray-700/50 bg-gray-900/70 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
          <Copyright currentYear={currentYear} />
          <DesktopLegalLinks />
        </div>
      </div>
    </div>
  );
};

// Footer Gradient Overlay Component
const FooterGradient = () => {
  return (
    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 to-transparent pointer-events-none"></div>
  );
};

// Main Footer Content Component
const MainFooterContent = ({ logoUrl }) => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8">
        <CompanyInfo logoUrl={logoUrl} />
        <FooterLinksGrid />
      </div>
      <MobileLegalLinks />
    </div>
  );
};

// Main Footer Component
export default function Footer({ logoUrl }) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gray-900/50 backdrop-blur border-t border-gray-700/50 mt-20">
      <MainFooterContent logoUrl={logoUrl} />
      <FooterBottom currentYear={currentYear} />
      <FooterGradient />
    </footer>
  );
}
